import * as Api from 'api/analytics';
import {
  CHANGE_DATE,
  CHANGE_LANGUAGE_FILTER,
  CHANGE_PAGE,
  SET_GROUP_FILTER,
  SET_LIMIT
} from 'actions/platformUsage/actions';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { getAuth, hasAnalyticsPermission } from 'actions/auth/login';
import {
  getEndTimeRequest,
  getGroupFilterID,
  getLanguageFilterCode,
  getLimit,
  getSkip,
  getStartTimeRequest,
  getUtcOffset
} from 'actions/platformUsage/selectors';
import Raven from 'raven-js';
import { delay } from 'redux-saga';
import { getLocale } from 'actions/language/selectors';
import { pushFormattedError } from 'helpers/formValidationHelper/formValidationMessage';
import { ERROR_ACCESS_DENIED } from 'lib/errorTypes';

// Actions
export const ERROR_FETCHING_DATA =
  'PLATFORM_USAGE_ANALYTICS/ERROR_FETCHING_DATA';
export const FETCH_DATA = 'PLATFORM_USAGE_ANALYTICS/FETCH_DATA';
export const SET_LOADING = 'UI/PLATFORM_USAGE_ANALYTICS/SET_LOADING';
export const SUCCESS_FETCHING_DATA =
  'PLATFORM_USAGE_ANALYTICS/SUCCESS_FETCHING_DATA';

/*** Action Creators ***/
export const fetchData = () => ({
  type: FETCH_DATA
});

export const fetchDataSuccess = (
  viewsByDate,
  viewsByPage,
  viewsBySession,
  viewsByOSVisits,
  viewsByBrowserVisits,
  hraCampaignStatisticsStart,
  hraCampaignStatisticsComplete,
  surveyStatisticsStart,
  surveyStatisticsComplete
) => ({
  type: SUCCESS_FETCHING_DATA,
  viewsByDate,
  viewsByPage,
  viewsBySession,
  viewsByOSVisits,
  viewsByBrowserVisits,
  hraCampaignStatisticsStart,
  hraCampaignStatisticsComplete,
  surveyStatisticsStart,
  surveyStatisticsComplete
});

export const fetchDataError = (error) => ({
  type: ERROR_FETCHING_DATA,
  error: error
});

export const setIsLoading = () => ({
  type: SET_LOADING
});

/*** Reducer ***/
export const DEFAULT_STATE = {
  viewsByDate: [],
  viewsByPage: { total: 0, pages: [] },
  viewsBySession: [],
  viewsByOSVisits: [],
  viewsByBrowserVisits: [],
  hraCampaignStatisticsComplete: [],
  hraCampaignStatisticsStart: [],
  surveyStatisticsComplete: [],
  surveyStatisticsStart: [],
  isLoading: false
};

export default function AnalyticsReducer(
  state = DEFAULT_STATE,
  {
    viewsByDate,
    viewsByPage,
    viewsBySession,
    viewsByOSVisits,
    viewsByBrowserVisits,
    hraCampaignStatisticsStart,
    hraCampaignStatisticsComplete,
    surveyStatisticsStart,
    surveyStatisticsComplete,
    error,
    type
  } = {}
) {
  switch (type) {
    case ERROR_FETCHING_DATA:
      Raven.captureException(error, { state });
      return {
        ...state,
        isLoading: false,
        error
      };

    case SUCCESS_FETCHING_DATA:
      return {
        ...state,
        viewsByDate,
        viewsByPage,
        viewsBySession,
        viewsByOSVisits,
        viewsByBrowserVisits,
        hraCampaignStatisticsStart,
        hraCampaignStatisticsComplete,
        surveyStatisticsStart,
        surveyStatisticsComplete,
        isLoading: false
      };
    case SET_LOADING:
      return {
        ...state,
        isLoading: true
      };

    default:
      return state;
  }
}

/*** Sagas ***/
// Worker
export function* reloadPlatformUsageData() {
  yield put(fetchData());
}
export function* fetchPlatformUsageWorker() {
  try {
    yield call(delay, 0);

    const authorization = yield select(getAuth);
    const locale = yield select(getLocale);
    const startDate = yield select(getStartTimeRequest);
    const endDate = yield select(getEndTimeRequest);
    const utcOffset = yield select(getUtcOffset);
    const userGroupId = yield select(getGroupFilterID);
    const selectedLanguage = yield select(getLanguageFilterCode);
    const limit = yield select(getLimit);
    const skip = yield select(getSkip);
    const hasPermission = yield select(hasAnalyticsPermission);

    if (!authorization || !hasPermission) {
      yield pushFormattedError({ id: ERROR_ACCESS_DENIED });
      return;
    }

    yield put(setIsLoading());
    const views = yield call(
      Api.getViews,
      authorization,
      locale,
      startDate,
      endDate,
      utcOffset,
      selectedLanguage,
      limit,
      skip,
      userGroupId
    );

    yield put(
      fetchDataSuccess(
        views.data.viewsByDate,
        views.data.viewsByPage,
        views.data.viewsBySession,
        views.data.viewsByOSVisits,
        views.data.viewsByBrowserVisits,
        views.data.hraCampaignStatisticsStart,
        views.data.hraCampaignStatisticsComplete,
        views.data.surveyStatisticsStart,
        views.data.surveyStatisticsComplete
      )
    );
  } catch (err) {
    if (
      err.errors &&
      err.errors.some((error) => error.message == 'Access denied error.')
    ) {
      // does not have platform analytics scope
      yield pushFormattedError({ id: ERROR_ACCESS_DENIED });
    } else {
      yield pushFormattedError(err);
    }
  }
}

// Watcher
export function* fetchPlatformUsageDataSaga() {
  yield all([
    takeLatest(FETCH_DATA, fetchPlatformUsageWorker),
    takeLatest(CHANGE_DATE, reloadPlatformUsageData),
    takeLatest(CHANGE_LANGUAGE_FILTER, reloadPlatformUsageData),
    takeLatest(CHANGE_PAGE, reloadPlatformUsageData),
    takeLatest(SET_LIMIT, reloadPlatformUsageData),
    takeLatest(SET_GROUP_FILTER, reloadPlatformUsageData)
  ]);
}



// WEBPACK FOOTER //
// ./src/actions/platformUsageAnalytics/platformUsageAnalytics.js